import React, { useState } from 'react';
import type { Order } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import XCircleIcon from '../icons/XCircleIcon';
import ClockIcon from '../icons/ClockIcon';

interface BuffetExtensionModalProps {
  order: Order;
  currentUser?: { id: number; permissions?: { has: (permission: string) => boolean } };
  onConfirm: (extensionMinutes: number, reason?: string) => void;
  onCancel: () => void;
}

const BuffetExtensionModal: React.FC<BuffetExtensionModalProps> = ({
  order,
  currentUser,
  onConfirm,
  onCancel,
}) => {
  const [selectedMinutes, setSelectedMinutes] = useState<number>(15);
  const [customMinutes, setCustomMinutes] = useState<string>('');
  const [useCustom, setUseCustom] = useState(false);
  const [reason, setReason] = useState('');
  const customMinutesKeyboard = useSoftKeyboardField({
    id: 'buffet-extension-minutes',
    type: 'numeric',
    label: 'Custom minutes',
    value: customMinutes,
    onChange: setCustomMinutes,
    manualValidator: (v) => v === '' || /^\d*$/.test(v),
  });
  const reasonKeyboard = useSoftKeyboardField({
    id: 'buffet-extension-reason',
    type: 'alphanumeric',
    label: 'Reason',
    value: reason,
    onChange: setReason,
  });

  const presetOptions = [15, 30, 45, 60];

  const handleConfirm = () => {
    const extensionMinutes = useCustom ? parseInt(customMinutes) || 0 : selectedMinutes;
    if (extensionMinutes <= 0) {
      alert('Please select a valid extension time.');
      return;
    }
    onConfirm(extensionMinutes, reason || undefined);
  };

  const currentEndTime = order.buffetEndTime || 0;
  const currentEndDate = new Date(currentEndTime);
  const newEndTime = currentEndTime + (useCustom ? (parseInt(customMinutes) || 0) : selectedMinutes) * 60 * 1000;
  const newEndDate = new Date(newEndTime);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto my-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">Extend Buffet Time</h2>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-800 text-2xl">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Current End Time</p>
            <p className="text-lg font-semibold text-gray-800">
              {currentEndDate.toLocaleTimeString('en-NZ', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Extension Duration
            </label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {presetOptions.map((minutes) => (
                <button
                  key={minutes}
                  onClick={() => {
                    setUseCustom(false);
                    setSelectedMinutes(minutes);
                  }}
                  className={`px-3 py-2 rounded-lg border-2 transition-colors ${
                    !useCustom && selectedMinutes === minutes
                      ? 'border-pink-500 bg-pink-50 text-pink-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {minutes} min
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="custom-time"
                checked={useCustom}
                onChange={(e) => setUseCustom(e.target.checked)}
                className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
              />
              <label htmlFor="custom-time" className="text-sm text-gray-700">
                Custom time (minutes)
              </label>
            </div>
            {useCustom && (
              <input
                type="number"
                min="1"
                value={customMinutes}
                placeholder="Enter minutes"
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                data-soft-keyboard-handled
                {...customMinutesKeyboard}
              />
            )}
          </div>

          {newEndTime > currentEndTime && (
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <p className="text-sm text-gray-600 mb-1">New End Time</p>
              <p className="text-lg font-semibold text-green-700">
                {newEndDate.toLocaleTimeString('en-NZ', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              placeholder="Enter reason for extension..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              data-soft-keyboard-handled
              {...reasonKeyboard}
            />
          </div>
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-semibold flex items-center gap-2"
          >
            <ClockIcon className="w-4 h-4" />
            Extend Time
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuffetExtensionModal;

